import { createFileRoute, Link, useParams } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { ArrowLeft, Download, FileText, Loader2 } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import {
  documentMediaQueryOptions,
  documentsQueryOptions,
  kindLabel,
} from "@/core/documents"

export const Route = createFileRoute("/$orgSlug/$eventSlug/doc/$documentId")({
  component: DocumentRecordView,
})

function DocumentRecordView() {
  const { orgSlug, eventSlug, documentId } = useParams({
    from: "/$orgSlug/$eventSlug/doc/$documentId",
  })
  const { eventId } = useActiveEvent()
  const documents = useQuery({
    ...documentsQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const document = documents.data?.documents.find((item) => item.id === documentId)
  const media = useQuery({
    ...documentMediaQueryOptions(eventId ?? "", document?.mediaAssetId ?? ""),
    enabled: !!eventId && !!document?.mediaAssetId,
  })

  if (!document) {
    return (
      <div className="flex h-full items-center justify-center gap-2 bg-white text-[13px] text-plum-700/55 dark:bg-surface dark:text-plum-200/45">
        {documents.isError ? (
          <span>{(documents.error as Error)?.message ?? "Failed to load document"}</span>
        ) : documents.isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading document…
          </>
        ) : (
          <span>Document not found.</span>
        )}
      </div>
    )
  }

  return (
    <div className="flex h-full min-w-0 flex-col bg-surface">
      <div className="flex h-[52px] shrink-0 items-center gap-3 border-b border-plum-200/50 bg-white px-4 dark:border-white/10 dark:bg-surface">
        <Link
          to="/$orgSlug/$eventSlug/documents"
          params={{ orgSlug, eventSlug }}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-plum-700/60 transition hover:bg-plum-100/60 dark:text-plum-200/50 dark:hover:bg-white/5"
          title="Back to documents"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <FileText className="h-4 w-4 shrink-0 text-[var(--accent)]" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold text-plum-900 dark:text-plum-50">
            {document.title || "Untitled document"}
          </div>
          <div className="truncate text-[11.5px] text-plum-700/55 dark:text-plum-200/45">
            {kindLabel(document.kind)} · {document.status === "published" ? "Published" : "Draft"}
          </div>
        </div>
      </div>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-5 @[700px]:p-8">
        <section className="mx-auto max-w-2xl overflow-hidden rounded-2xl border border-plum-200/60 bg-white shadow-fluent-sm dark:border-white/10 dark:bg-white/[0.02]">
          <div className="flex items-start gap-4 px-5 py-5">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-plum-100 text-plum-700 dark:bg-white/10 dark:text-plum-100">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-[17px] font-semibold text-plum-900 dark:text-plum-50">
                {document.title}
              </h1>
              <p className="mt-1 text-[12.5px] text-plum-700/55 dark:text-plum-200/45">
                {media.data?.media.originalFilename ?? "Uploaded event document"}
                {media.data?.media.byteSize
                  ? ` · ${formatBytes(media.data.media.byteSize)}`
                  : ""}
              </p>
              {document.summary && (
                <p className="mt-3 text-[13px] leading-5 text-plum-700/75 dark:text-plum-200/65">
                  {document.summary}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end border-t border-plum-200/50 px-5 py-3.5 dark:border-white/10">
            {media.isLoading ? (
              <span className="flex items-center gap-2 text-[12.5px] text-plum-700/50 dark:text-plum-200/40">
                <Loader2 className="h-4 w-4 animate-spin" />
                Preparing download…
              </span>
            ) : media.data?.url ? (
              <a
                href={media.data.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-[var(--accent)] px-4 text-[13px] font-semibold text-white shadow-sm transition hover:brightness-105"
              >
                <Download className="h-4 w-4" />
                Open document
              </a>
            ) : (
              <span className="text-[12.5px] text-rose-600 dark:text-rose-400">
                {media.isError
                  ? ((media.error as Error)?.message ?? "Could not prepare this document")
                  : "This document is not ready to download."}
              </span>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}
